import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, ListSubheader, Paper, Collapse, Link, FormControlLabel, Checkbox,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { adminClient } from '../data/adminClient';
import RichTextEditor from './RichTextEditor';

// Whether the message has any words (it's HTML from the editor, or plain text).
const hasWords = (s) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim().length > 0;

const isEmail = (v) => /^\S+@\S+\.\S+$/.test(v || '');
const BLANK = '__blank__';

// One place to email leaders: pick a built-in email ("Ask if interested",
// "Send info link"), one of your own saved templates, or start a blank
// one-off email. Edits can be saved back to the template, and a one-off can
// be saved as a new template, so wording carries over for next time.
// initialTemplateId (optional) pre-selects an email, e.g. 'contract'.
export default function LeaderEmailDialog({ open, recipients, onClose, onSent, initialTemplateId }) {
  const [settings, setSettings] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saveChanges, setSaveChanges] = useState(true);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [editSender, setEditSender] = useState(false);
  const [previewFor, setPreviewFor] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const [editorKey, setEditorKey] = useState(0);
  // Puts a template's text into the editor (which only reads it on mount).
  const loadBody = (text) => { setBody(text); setEditorKey(k => k + 1); };

  const withEmail = recipients.filter(r => isEmail(r.profile.email));
  const withoutEmail = recipients.filter(r => !isEmail(r.profile.email));
  const template = settings?.templates.find(t => t.id === templateId) || null;
  const isBlank = templateId === BLANK;
  const edited = template && (subject !== template.subject || body !== template.body);

  const applySettings = (s, selectId) => {
    setSettings(s);
    const pick = s.templates.find(t => t.id === selectId);
    if (pick) { setTemplateId(pick.id); setSubject(pick.subject); loadBody(pick.body); }
  };

  useEffect(() => {
    if (!open) return;
    setSettings(null); setResult(null); setError(null); setPreview(null); setNotice('');
    setEditSender(false); setConfirmDelete(false); setNewTemplateName(''); setSaveChanges(true);
    setPreviewFor(recipients[0]?.engagementId || '');
    setTemplateId(''); setSubject(''); loadBody('');
    adminClient.getWorkshopEmailSettings()
      .then(s => {
        setSettings(s);
        const initial = initialTemplateId && s.templates.find(t => t.id === initialTemplateId);
        if (initial) { setTemplateId(initial.id); setSubject(initial.subject); loadBody(initial.body); }
        setSenderName(s.senderName);
        setReplyTo(s.replyTo);
        if (!s.replyTo) setEditSender(true);
      })
      .catch(err => setError(err.message));
    // recipients is a fresh array each render; only reload when (re)opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Live preview for the chosen leader, a moment after typing stops.
  useEffect(() => {
    if (!open || !settings || !previewFor || !subject.trim() || !hasWords(body)) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      adminClient.previewWorkshopEmail({ engagementId: previewFor, subject, body })
        .then(p => { setPreview(p); setPreviewError(null); })
        .catch(err => setPreviewError(err.message));
    }, 500);
    return () => clearTimeout(t);
  }, [open, settings, previewFor, subject, body]);

  const chooseTemplate = (id) => {
    setTemplateId(id); setConfirmDelete(false); setNotice(''); setSaveChanges(true); setNewTemplateName('');
    if (id === BLANK) { setSubject(''); loadBody(''); return; }
    const t = settings.templates.find(x => x.id === id);
    if (t) { setSubject(t.subject); loadBody(t.body); }
  };

  // Inserts at the cursor in the message (or the end, if it hasn't been clicked).
  const insertPlaceholder = (name) => editorRef.current?.insertText(`{${name}}`);

  const run = async (fn) => {
    setBusy(true); setError(null); setNotice('');
    try { await fn(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const handleSaveTemplate = () => run(async () => {
    const s = await adminClient.saveWorkshopEmailTemplate(isBlank
      ? { label: newTemplateName.trim(), subject, body }
      : { id: template.id, label: template.label, subject, body });
    applySettings(s, s.savedId);
    setNewTemplateName('');
    setNotice('Template saved.');
  });

  const handleDeleteTemplate = () => run(async () => {
    const s = await adminClient.deleteWorkshopEmailTemplate(template.id);
    setSettings(s);
    setConfirmDelete(false);
    chooseTemplate(BLANK);
    setNotice('Template deleted.');
  });

  const senderChanged = settings && (senderName !== settings.senderName || replyTo !== settings.replyTo);

  const handleSend = () => run(async () => {
    if (senderChanged) await adminClient.saveWorkshopEmailSettings({ senderName, replyTo });
    const res = await adminClient.sendWorkshopEmail({
      templateId: isBlank ? undefined : templateId,
      subject, body,
      engagementIds: withEmail.map(r => r.engagementId),
      saveTemplate: isBlank ? undefined : saveChanges,
      newTemplateName: isBlank ? newTemplateName.trim() : undefined,
    });
    setResult(res);
    onSent();
  });

  const builtIns = settings?.templates.filter(t => t.builtIn) || [];
  const own = settings?.templates.filter(t => !t.builtIn) || [];
  const ready = settings && templateId && subject.trim() && hasWords(body);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email {recipients.length === 1 ? recipients[0].profile.name : `${recipients.length} leaders`}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
        {result ? (
          <Box>
            <Alert severity={result.sent.length ? 'success' : 'warning'} sx={{ mb: 2 }}>
              Sent to {result.sent.length} leader{result.sent.length === 1 ? '' : 's'}.
              {result.stoppedEarly && ' Sending stopped because the email limit was reached — try the rest later.'}
            </Alert>
            {result.skipped.length > 0 && (
              <>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Not sent</Typography>
                {result.skipped.map((s, i) => <Typography key={i} variant="body2">{s.name} — {s.reason}</Typography>)}
              </>
            )}
          </Box>
        ) : !settings ? (
          !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Email to send</InputLabel>
                <Select label="Email to send" value={templateId} onChange={e => chooseTemplate(e.target.value)}>
                  <MenuItem value={BLANK}><em>New email (start blank)</em></MenuItem>
                  <ListSubheader>Built-in</ListSubheader>
                  {builtIns.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                  {own.length > 0 && <ListSubheader>Your templates</ListSubheader>}
                  {own.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>

              {templateId && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    To: <strong>{withEmail.length} leader{withEmail.length === 1 ? '' : 's'}</strong>
                    {withEmail.length <= 4 && withEmail.length > 0 && ` (${withEmail.map(r => r.profile.name).join(', ')})`}
                  </Typography>
                  {withoutEmail.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No email address for {withoutEmail.map(r => r.profile.name).join(', ')}, so they won’t get this email. Add one on their profile.
                    </Alert>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    From “{senderName || '—'}”{replyTo ? `, replies go to ${replyTo}` : ''}
                    {' · '}<Link component="button" variant="body2" onClick={() => setEditSender(v => !v)}>{editSender ? 'Hide' : 'Change'}</Link>
                  </Typography>
                  <Collapse in={editSender}>
                    <Box sx={{ display: 'grid', gap: 1.5, mt: 1.5 }}>
                      <TextField size="small" label="Sender name" value={senderName} onChange={e => setSenderName(e.target.value)} inputProps={{ maxLength: 80 }} />
                      <TextField size="small" label="Reply-to email (workshop coordinator)" value={replyTo} onChange={e => setReplyTo(e.target.value)}
                        error={!!replyTo && !isEmail(replyTo)}
                        helperText={replyTo ? (isEmail(replyTo) ? 'Leaders’ replies go here.' : 'That email address doesn’t look right') : 'Set this so leaders can reply to you.'} />
                    </Box>
                  </Collapse>

                  <TextField size="small" label="Subject" fullWidth value={subject} onChange={e => setSubject(e.target.value)} sx={{ mt: 2 }} inputProps={{ maxLength: 200 }} />
                  <Box sx={{ mt: 2 }}>
                    {/* Remounts (key) whenever a template is loaded into it. */}
                    <RichTextEditor key={editorKey} ref={editorRef} label="Message" minHeight={220}
                      initialValue={body} onChange={setBody} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>
                    Click to insert — filled in for each leader:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {settings.placeholders.map(p => <Chip key={p} size="small" label={`{${p}}`} onClick={() => insertPlaceholder(p)} />)}
                  </Box>

                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    {isBlank ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField size="small" fullWidth label="Save as a template (optional name)" value={newTemplateName}
                          onChange={e => setNewTemplateName(e.target.value)} inputProps={{ maxLength: 60 }}
                          helperText="Name it to reuse this email later — e.g. “Contract reminder”." />
                        <Button size="small" onClick={handleSaveTemplate} disabled={busy || !newTemplateName.trim() || !subject.trim() || !hasWords(body)}>Save</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {edited ? (
                          <>
                            <FormControlLabel sx={{ mr: 'auto' }}
                              control={<Checkbox size="small" checked={saveChanges} onChange={e => setSaveChanges(e.target.checked)} />}
                              label={<Typography variant="body2">Save my changes to “{template.label}” when I send</Typography>} />
                            <Button size="small" onClick={handleSaveTemplate} disabled={busy}>Save now</Button>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                            Saved template “{template.label}”.{template.builtIn ? ' Built-in emails can be reworded but not deleted.' : ''}
                          </Typography>
                        )}
                        {!template.builtIn && (confirmDelete ? (
                          <>
                            <Typography variant="body2">Delete this template?</Typography>
                            <Button size="small" color="error" variant="contained" onClick={handleDeleteTemplate} disabled={busy}>Delete</Button>
                            <Button size="small" onClick={() => setConfirmDelete(false)}>Keep</Button>
                          </>
                        ) : (
                          <Button size="small" color="error" onClick={() => setConfirmDelete(true)}>Delete template</Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {!templateId && (
                <Typography variant="body2" color="text.secondary">
                  Choose a built-in email or one of your templates, or start a new email.
                </Typography>
              )}
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>Preview</Typography>
                {recipients.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>As sent to</InputLabel>
                    <Select label="As sent to" value={previewFor} onChange={e => setPreviewFor(e.target.value)}>
                      {recipients.map(r => <MenuItem key={r.engagementId} value={r.engagementId}>{r.profile.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
                {previewError ? (
                  <Typography variant="body2" color="error">{previewError}</Typography>
                ) : !preview ? (
                  <Typography variant="body2" color="text.secondary">Preview appears here.</Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block">To: {preview.to || '(no email)'}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>{preview.subject}</Typography>
                    {/* Server-rendered: every typed value is HTML-escaped (see renderEmail in backend/workshops.js). */}
                    <Box sx={{ fontSize: 14, '& a': { wordBreak: 'break-all' } }} dangerouslySetInnerHTML={{ __html: preview.html }} />
                  </>
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" startIcon={busy ? null : <SendIcon />} onClick={handleSend}
              disabled={!ready || busy || withEmail.length === 0 || (!!replyTo && !isEmail(replyTo))}>
              {busy ? <CircularProgress size={18} color="inherit" /> : `Send to ${withEmail.length}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
